import { useState } from 'react';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send,
  Clock,
  MessageSquare,
  CheckCircle2,
  ArrowRight,
  Linkedin,
  Twitter,
  Github
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL =  'https://www.jnanit.com/send_mail.php';
const API = `${BACKEND_URL}`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    serviceInterested: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, serviceInterested: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Get reCAPTCHA v3 token
    if (!window.grecaptcha) {
      toast.error('reCAPTCHA is not loaded. Please refresh the page.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Execute reCAPTCHA v3
      const recaptchaToken = await window.grecaptcha.execute('6Lcg8DEqAAAAAMtIx0uM-Uis4EjGfvsEei-aup4P', { action: 'submit' });
      
      if (!recaptchaToken) {
        toast.error('reCAPTCHA verification failed.');
        setIsSubmitting(false);
        return;
      }

      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('message', formData.message);
      formDataToSend.append('serviceInterested', formData.serviceInterested);
      formDataToSend.append('g-recaptcha-response', recaptchaToken);

      console.log('Sending form to:', API);
      
      const response = await fetch(API, {
        method: 'POST',
        body: formDataToSend,
      });

      console.log('Response status:', response.status);

      if (response.ok || response.status === 301 || response.status === 302) {
        toast.success('Message sent successfully! We\'ll get back to you soon.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          serviceInterested: '',
        });
      } else {
        console.error('Response not ok:', response.status);
        toast.error('Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('An error occurred. Failed to send message - CORS issue with backend. Contact support.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email Us',
      value: 'info@jnanit.com',
      link: 'mailto:info@jnanit.com',
      color: 'from-blue-500 to-indigo-600',
    },
    {
      icon: Phone,
      title: 'Call Us',
      value: '+1 (747)-343-1011',
      link: 'tel:+17473431011',
      color: 'from-purple-500 to-pink-600',
    },
   /*  {
      icon: MapPin,
      title: 'Visit Us',
      value: '123 Innovation Street, Tech Valley, CA 94000',
      link: '#',
      color: 'from-orange-500 to-red-600',
    }, */
    {
      icon: Clock,
      title: 'Business Hours',
      value: 'Mon - Fri: 9:00 AM - 6:00 PM',
      link: '#',
      color: 'from-teal-500 to-cyan-600',
    },
  ];

  return (
    <div data-testid="contact-page" className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 hero-gradient mesh-gradient">
        <div className="absolute top-20 right-10 w-72 h-72 bg-purple-400/20 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block px-4 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-medium mb-4">
              Contact Us
            </span>
            <h1 
              data-testid="contact-hero-title"
              className="text-4xl md:text-6xl font-bold font-['Outfit'] tracking-tighter mb-6"
            >
              Let's Build Something{' '}
              <span className="gradient-text">Amazing Together</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Have a project in mind? We'd love to hear about it. Send us a message and 
              we'll get back to you as soon as possible.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white -mt-12 relative z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {contactInfo.map((info, index) => (
              <Card 
                key={index}
                data-testid={`contact-info-${index}`}
                className="bg-white rounded-2xl shadow-lg border-0 hover:shadow-xl transition-shadow"
              >
                <CardContent className="p-6">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${info.color} flex items-center justify-center mb-4`}>
                    <info.icon className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="font-bold font-['Outfit'] text-slate-900 mb-1">{info.title}</h4>
                  <a 
                    href={info.link}
                    className="text-slate-600 hover:text-blue-600 transition-colors text-sm"
                  >
                    {info.value}
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section data-testid="contact-form-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Left Content */}
            <div>
              <span className="inline-block px-4 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-medium mb-4">
                Get in Touch
              </span>
              <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight mb-6">
                Ready to Start Your <span className="gradient-text">Project?</span>
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Fill out the form and our team will get back to you within 24 hours. 
                We're excited to learn about your project and discuss how we can help.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Quick Response</h4>
                    <p className="text-slate-600 text-sm">We respond to all inquiries within 24 hours.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Free Consultation</h4>
                    <p className="text-slate-600 text-sm">Get a free 30-minute consultation call.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Flexible Scheduling</h4>
                    <p className="text-slate-600 text-sm">We work with clients across all time zones.</p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="mt-12 pt-8 border-t border-slate-200">
                <p className="text-sm text-slate-500 mb-4">Connect with us on social media</p>
                <div className="flex gap-4">
                  {[
                    { icon: Linkedin, href: 'https://www.linkedin.com/company/jnanit/', target: '_blank' },
                    /* { icon: Twitter, href: '#' },
                    { icon: Github, href: '#' }, */
                  ].map((social, index) => (
                    <a
                      key={index}
                      href={social.href}
                      className="w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                    >
                      <social.icon className="w-5 h-5 text-slate-600" />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <Card className="bg-white rounded-3xl shadow-xl border-0">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Your Name *
                        </label>
                        <Input
                          data-testid="contact-name-input"
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Doe"
                          required
                          className="rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Email Address *
                        </label>
                        <Input
                          data-testid="contact-email-input"
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@example.com"
                          required
                          className="rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Phone Number *
                      </label>
                      <Input
                        data-testid="contact-phone-input"
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+1 (555) 123-4567"
                        required
                        className="rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Service Interested *
                      </label>
                      <Select value={formData.serviceInterested} onValueChange={handleServiceChange}>
                        <SelectTrigger className="rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ai-integration">AI Integration</SelectItem>
                          <SelectItem value="custom-integrations">Custom Integrations</SelectItem>
                          <SelectItem value="shopify-solutions">Shopify Solutions</SelectItem>
                          <SelectItem value="netsuite-integration">NetSuite Integration</SelectItem>
                          <SelectItem value="web-development">Web Development</SelectItem>
                          <SelectItem value="app-development">App Development</SelectItem>
                          <SelectItem value="it-services">IT Services & Support</SelectItem>
                          <SelectItem value="erp-solutions">ERP Solutions</SelectItem>
                          <SelectItem value="cloud-infrastructure">Cloud Infrastructure</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Your Message *
                      </label>
                      <Textarea
                        data-testid="contact-message-input"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your project..."
                        required
                        rows={5}
                        className="rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500 resize-none"
                      />
                    </div>

                    <Button
                      data-testid="contact-submit-button"
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-full py-6 font-semibold shadow-lg shadow-blue-500/25 group"
                    >
                      {isSubmitting ? (
                        'Sending...'
                      ) : (
                        <>
                          Send Message
                          <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </Button>

                    {/* <p className="text-center text-sm text-slate-500">
                      By submitting this form, you agree to our{' '}
                      <a href="#" className="text-blue-600 hover:underline">Privacy Policy</a>
                    </p> */}
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section Placeholder */}
     {/*  <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight">
              Find Us <span className="gradient-text">Here</span>
            </h2>
          </div>
          
          <div className="rounded-3xl overflow-hidden shadow-xl">
            <div className="h-96 bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center">
              <div className="text-center">
                <MapPin className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-500 font-medium">123 Innovation Street</p>
                <p className="text-slate-500">Tech Valley, CA 94000</p>
                <Button 
                  variant="outline"
                  className="mt-4 rounded-full"
                  onClick={() => window.open('https://maps.google.com', '_blank')}
                >
                  Open in Google Maps
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="py-24 cta-gradient relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight text-white mb-6">
            Prefer a Quick Call?
          </h2>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            Schedule a free 30-minute consultation call with our team to discuss your project.
          </p>
          <Button 
            data-testid="schedule-call-button"
            size="lg"
            className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-6 text-lg font-semibold shadow-xl group"
            onClick={() => window.open('https://outlook.office.com/bookwithme/user/25344daed1354d7484a1a37962d064e4@jnanit.com/meetingtype/Qalgu0vlnkeyfK9F0RqPFQ2?anonymous&ismsaljsauthenabled&ep=mlink', '_blank')}
          >
            Schedule a Call
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Contact;
